/**
 * Composable for handling the rebooking process
 */
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useBookingStore } from '@/stores/bookingStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useErrorStore } from '@/stores/errorStore'
import { handleRebookingProcess } from '@/services/rebooking/rebookingService'
import { checkIfBookingHasSeats, getActiveSegments } from '@/utils/rebooking/dataTransformUtils'
import { Booking } from '@/types/bookingTypes'

/**
 * Helper function to detect if a booking has transfer journeys
 * Transfer journey = more than one active (non-cancelled) segment in either outbound or inbound direction
 */
function detectTransferJourney(booking: Booking): boolean {
  if (!booking) {
    return false
  }
  
  const activeOutboundSegments = getActiveSegments(booking.outbound_booking_tariff_segments)
  const activeInboundSegments = getActiveSegments(booking.inbound_booking_tariff_segments)
  const isTransfer = activeOutboundSegments.length > 1 || activeInboundSegments.length > 1

  return isTransfer
}

export function useRebookingProcess(bookingNumber: string) {
  const router = useRouter()
  const { t } = useI18n()
  const bookingStore = useBookingStore()
  const journeyStore = useJourneyStore()
  const errorStore = useErrorStore()
  
  /**
   * Main function to handle the rebooking process
   */
  const processRebooking = async (
    selectedTravel: any,
    selectedReturnTravel: any | null,
    itemsToReplace: string[] = [],
    setRebookingStarted: (value: boolean) => void,
    setErrorMessage: (message: string | undefined) => void,
    onSuccess: () => void
  ) => {
    // Set initial state
    setRebookingStarted(true)
    setErrorMessage(undefined)
    
    try {
      const result = await handleRebookingProcess(
        bookingNumber,
        selectedTravel,
        selectedReturnTravel,
        itemsToReplace
      )
      bookingStore.setBookingData(result.bookingData)
      journeyStore.setBookingData(result.bookingData)
      const isTransferJourney = detectTransferJourney(result.bookingData)
      journeyStore.setIsTransferJourney(isTransferJourney)
      onSuccess()

      
      // if (isTransferJourney) {
      //   // Transfer journeys skip seat reservation and go directly to EquipmentPage
      //   console.log('[REBOOKING_NAVIGATION] Transfer journey detected, routing to EquipmentPage')
      //   router.push({
      //     name: 'EquipmentPage',
      //     query: { isRebooking: 'true' },
      //   })
      // } else 
       if (result.hasSeats) {
      router.push({
          name: 'RebookingSeatReservation',
          params: { bookingNumber },
          query: { isRebooking: 'true' },
        })
      } else {
        router.push({
          name: 'EquipmentPage',
          query: { isRebooking: 'true' },
        })
      }
    } catch (error) {
      console.error('General rebooking error:', error)
      let errorMessage = t('afterSale.rebook.error')
      
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage
      }
      
      errorStore.setError(errorMessage)
      setErrorMessage(errorMessage)
      setRebookingStarted(false)
    }
  }
  
  return {
    processRebooking
  }
}
